import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Slider } from "@mui/material";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";

const RevenueSimulator = () => {
  const [traffic, setTraffic] = useState(10000);
  const [ctr, setCTR] = useState(1.5);
  const [ecpm, setECPM] = useState(2.5);
  const [projectedRevenue, setProjectedRevenue] = useState(0);

  useEffect(() => {
    const impressions = (traffic * (ctr / 100));
    const revenue = (impressions / 1000) * ecpm;
    setProjectedRevenue(revenue.toFixed(2));
  }, [traffic, ctr, ecpm]);

  const chartData = [
    { name: "Traffic", value: traffic },
    { name: "CTR", value: ctr },
    { name: "eCPM", value: ecpm },
    { name: "Revenue", value: parseFloat(projectedRevenue) }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="p-6 bg-white rounded-2xl shadow-2xl max-w-4xl mx-auto mt-12"
    >
      <h2 className="text-2xl font-bold text-gray-800 mb-4">💰 Revenue Simulator</h2>
      <p className="text-sm text-gray-500 mb-6">
        Adjust the parameters below to simulate your projected ad revenue.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Traffic (Daily Pageviews)</label>
          <Slider value={traffic} min={1000} max={100000} step={500} onChange={(e, val) => setTraffic(val)} />
          <p className="text-sm text-gray-600 mt-1">{traffic.toLocaleString()} views</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">CTR (%)</label>
          <Slider value={ctr} min={0.1} max={10} step={0.1} onChange={(e, val) => setCTR(val)} />
          <p className="text-sm text-gray-600 mt-1">{ctr.toFixed(1)}%</p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">eCPM ($)</label>
          <Slider value={ecpm} min={0.1} max={10} step={0.1} onChange={(e, val) => setECPM(val)} />
          <p className="text-sm text-gray-600 mt-1">${ecpm.toFixed(2)}</p>
        </div>
      </div>

      <div className="text-center mb-6">
        <motion.p
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.5 }}
          className="text-4xl font-bold text-green-600"
        >
          Projected Daily Revenue: ${projectedRevenue}
        </motion.p>
      </div>

      <div className="h-64">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData}>
            <XAxis dataKey="name" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="value" fill="#4F46E5" radius={[4, 4, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </motion.div>
  );
};

export default RevenueSimulator;
